import React from 'react';
import { Device } from '../../lib/types';
import type { SortDir } from '../../lib/types';
import DeviceRow from './DeviceRow';
import { SkeletonRows } from './SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import Button from '../../components/Button/Button';
import SortableHeader from './SortableHeader';
import CollapsibleSection from './CollapsibleSection';
import styles from './Devices.module.scss';

/** Columns this table can be sorted by (clicking their header). */
export type DeviceSortKey = 'holder' | 'pet' | 'device';

interface DeviceSectionTableProps {
  title: string;
  description: string;
  /** Plural noun used in the empty-state copy, e.g. "activated devices". */
  emptyLabel: string;
  /** Show the Holder column. False hides it (e.g. Activated, where the holder is always the rescue). */
  showHolder?: boolean;
  devices: Device[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  skeletonCount: number;
  searchQuery: string;
  /**
   * Enables click-to-sort on the Pet / Device headers. Provide all three to opt
   * in; omit them (e.g. the Fosters section) to render plain, unsortable
   * headers. `sortKey` is null when no column is active yet.
   */
  sortKey?: DeviceSortKey | null;
  sortDir?: SortDir;
  onSort?: (key: DeviceSortKey) => void;
  /** When true, replaces the rows with an error + Retry (used by the drained sort mode). */
  hasError?: boolean;
  onRetry?: () => void;
  /** Pet IDs whose adoption transfer is complete — used to hide row actions. */
  adoptedPetIds?: Set<string>;
  /** True while the adopted-pet lookup is in flight — gate actions optimistically. */
  adoptedLoading?: boolean;
  onAddPet: (device: Device) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  /** One-click adopt-out using the foster household's info; only the Fosters section wires this. */
  onAdoptFromFoster?: (device: Device) => void;
  onSendSms: (device: Device) => void;
}

/**
 * A section card listing devices attached to pets (via {@link DeviceRow}).
 * Shared by the "Activated" and "Devices with a Foster" panels — they differ
 * only in copy and which server-paginated list feeds them.
 */
export default function DeviceSectionTable({
  title,
  description,
  emptyLabel,
  showHolder = true,
  devices,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  skeletonCount,
  searchQuery,
  sortKey,
  sortDir,
  onSort,
  hasError,
  onRetry,
  adoptedPetIds,
  adoptedLoading,
  onAddPet,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onAdoptFromFoster,
  onSendSms,
}: DeviceSectionTableProps) {
  const columns: { label: string; width: string; sortKey?: DeviceSortKey }[] = showHolder
    ? [
        { label: 'Holder', width: '14%', sortKey: 'holder' },
        { label: 'Pet', width: '13%', sortKey: 'pet' },
        { label: 'Device', width: '14%', sortKey: 'device' },
        { label: 'Setup', width: '10%' },
        { label: 'Status', width: '12%' },
        { label: 'Battery', width: '10%' },
        { label: 'Action', width: '27%' },
      ]
    : [
        { label: 'Pet', width: '16%', sortKey: 'pet' },
        { label: 'Device', width: '16%', sortKey: 'device' },
        { label: 'Setup', width: '12%' },
        { label: 'Status', width: '14%' },
        { label: 'Battery', width: '12%' },
        { label: 'Action', width: '30%' },
      ];
  const colCount = columns.length;
  const rowProps = {
    onAddPet,
    onTransferFoster,
    onRecall,
    onTransferAdopter,
    onAdoptFromFoster,
    onSendSms,
    adoptedPetIds,
    adoptedLoading,
    showHolder,
  };

  // Use the dimming overlay when we have stale data to keep visible (prevents a
  // height jump); only fall back to skeleton rows when there's no data at all.
  const hasData = devices.length > 0;
  const showOverlay = skeletonCount > 0 && hasData;
  const showSkeleton = skeletonCount > 0 && !hasData;
  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <CollapsibleSection title={title} count={totalCount} description={description}>
      <div className={wrapperClass}>
        <table className={styles.table}>
          <colgroup>
            {columns.map((col) => (
              <col key={col.label} style={{ width: col.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => {
                const key = col.sortKey;
                // Sortable only when the column declares a key AND the parent
                // opted in with an onSort handler (the Fosters section omits it).
                if (!key || !onSort) {
                  return <th key={col.label}>{col.label}</th>;
                }
                return (
                  <SortableHeader
                    key={col.label}
                    label={col.label}
                    active={sortKey === key}
                    dir={sortDir}
                    onClick={() => onSort(key)}
                  />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {hasError ? (
              <tr>
                <td colSpan={colCount} className={styles.errorRow} role="alert">
                  <span className={styles.errorRowContent}>
                    <span>Could not load {emptyLabel}.</span>
                    {onRetry && (
                      <Button variant="ghost" size="sm" onClick={onRetry}>
                        Retry
                      </Button>
                    )}
                  </span>
                </td>
              </tr>
            ) : showSkeleton ? (
              <SkeletonRows count={skeletonCount} columns={colCount} />
            ) : devices.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No ${emptyLabel} matching "${searchQuery}"` : `No ${emptyLabel}`}
                </td>
              </tr>
            ) : (
              devices.map((d) => <DeviceRow key={d.id} device={d} {...rowProps} />)
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </CollapsibleSection>
  );
}
